<template>
  <div class="w-full max-w-3xl mx-auto py-6 px-6">
    <div class="flex items-center justify-between mb-3">
      <button
        class="text-sm text-gray-500 hover:text-dark-primary"
        @click="backToList"
      >&larr; Back to {{ backLabel }}</button>
      <StatusPill v-if="record" :status="record.status" />
    </div>

    <div class="bg-white border border-gray-300 rounded-lg shadow-sm p-6">
      <h2 class="text-lg font-semibold mb-4">
        {{ isNew ? "New Expense" : `Expense #${record ? record.id : ''}` }}
      </h2>

      <div v-if="loading" class="text-center text-gray-500 py-10">Loading...</div>

      <div v-else-if="record && record.status === 'rejected' && record.rejection_reason"
           class="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
        <div class="text-sm font-semibold text-red-700 mb-1">Rejected — please revise and resubmit</div>
        <div class="text-sm text-red-700 whitespace-pre-wrap">{{ record.rejection_reason }}</div>
      </div>

      <form v-if="!loading" class="space-y-5" @submit.prevent="saveDraft">
        <!-- Submit-for picker (only when user has active delegations from others) -->
        <div v-if="isNew && myDelegators.length > 0">
          <label class="block text-sm mb-1">Submit for</label>
          <select
            v-model="form.on_behalf_of_id"
            class="w-full max-w-md p-2 border border-gray-400 rounded-md"
          >
            <option :value="null">Myself</option>
            <option v-for="u in myDelegators" :key="u.id" :value="u.id">
              {{ u.full_name }} ({{ u.username }})
            </option>
          </select>
          <div class="text-xs text-gray-500 mt-1">
            If you pick someone else, the record will belong to them — it routes to
            their manager and reimburses them. The audit trail records that you
            entered it on their behalf.
          </div>
        </div>

        <div
          v-else-if="record && record.created_by && record.submitter && record.created_by.id !== record.submitter.id"
          class="p-2 rounded bg-blue-50 border border-blue-200 text-xs text-blue-800"
        >
          This record was entered by
          <b>{{ fullName(record.created_by) }}</b>
          on behalf of
          <b>{{ fullName(record.submitter) }}</b>.
        </div>

        <div
          v-if="record && !canActOnRecord"
          class="p-2 rounded bg-gray-100 border border-gray-300 text-xs text-gray-700"
        >
          Read-only view — you can see this record but cannot edit it or take
          submitter actions.
        </div>

        <div class="border border-gray-300 rounded-md p-4 bg-white">
          <div class="text-lg font-semibold mb-2">1. Expense Details</div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm mb-1">Date of expense</label>
              <input
                v-model="form.date_of_expense"
                type="date"
                :disabled="!editable"
                class="w-full p-2 border border-gray-400 rounded-md"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Amount</label>
              <div class="grid grid-cols-2 gap-2">
                <div class="relative">
                  <input
                    v-model.number="form.foreign_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    :disabled="!editable"
                    class="w-full p-2 pr-16 border border-gray-400 rounded-md [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="0.00"
                    @input="onAmountInput"
                  />
                  <span
                    v-if="isForeignCurrency && !conversionLoading && !conversionError && form.amount != null"
                    class="absolute inset-y-0 right-2 flex items-center text-xs text-gray-500 pointer-events-none"
                  >${{ formatUsd }}</span>
                </div>
                <select
                  v-model="form.foreign_currency_code"
                  :disabled="!editable"
                  class="w-full p-2 border border-gray-400 rounded-md bg-white"
                  @change="onCurrencyChange"
                >
                  <option v-for="c in supportedCurrencies" :key="c.code" :value="c.code">{{ c.code }} - {{ c.name }}</option>
                </select>
              </div>
              <div v-if="isForeignCurrency" class="text-xs mt-1">
                <div v-if="conversionLoading" class="text-gray-500">Looking up rate…</div>
                <div v-else-if="conversionError" class="text-red-700">{{ conversionError }}</div>
                <div v-else-if="form.exchange_rate && form.amount != null" class="text-gray-500">
                  {{ formatForeign }} @ {{ form.exchange_rate }} = ${{ formatUsd }} USD
                  <template v-if="form.exchange_rate_source">· {{ form.exchange_rate_source }}{{ form.exchange_rate_date ? ' · rate of ' + form.exchange_rate_date : '' }}</template>
                </div>
                <div v-else class="text-gray-500">Enter an amount — we'll fetch the rate for {{ form.date_of_expense || 'the expense date' }}.</div>
              </div>
            </div>
            <div>
              <label class="block text-sm mb-1">Description</label>
              <input
                v-model="form.description"
                type="text"
                maxlength="255"
                :disabled="!editable"
                class="w-full p-2 border border-gray-400 rounded-md"
                placeholder="e.g. Dinner at Marco's Grill"
              />
            </div>
            <div>
              <label class="block text-sm mb-1">Category</label>
              <select
                v-model="form.category_id"
                :disabled="!editable"
                class="w-full p-2 border border-gray-400 rounded-md"
              >
                <option :value="null" disabled>Select category...</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="border border-gray-300 rounded-md p-4 bg-white mb-4">
          <div class="text-lg font-semibold mb-2">2. Payment &amp; Reimbursement</div>
          <div class="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4">
          <div>
            <div class="text-sm font-semibold mb-2">Paid by</div>
            <div class="text-sm space-y-3">
              <label class="flex items-start gap-2">
                <input
                  type="radio"
                  value="personal_paid"
                  v-model="form.payment_method"
                  :disabled="!editable"
                  class="mt-1"
                />
                <span>
                  <span class="font-medium">Me (personally)</span>
                  <span class="block text-xs text-gray-500">Reimbursed to me by Payroll after Accounting approves.</span>
                </span>
              </label>
              <label class="flex items-start gap-2">
                <input
                  type="radio"
                  value="company_paid"
                  v-model="form.payment_method"
                  :disabled="!editable"
                  class="mt-1"
                />
                <span>
                  <span class="font-medium">Company card / debit</span>
                  <span class="block text-xs text-gray-500">No personal reimbursement; Accounting reconciles against the bank statement.</span>
                </span>
              </label>
            </div>
          </div>

          <div class="hidden md:block w-px bg-gray-200"></div>

          <div>
            <div class="text-sm divide-y divide-gray-200">
              <div class="py-3 first:pt-0 last:pb-0">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="form.is_client_reimbursable"
                    type="checkbox"
                    :disabled="!editable"
                  />
                  <span class="font-medium">Client reimbursable</span>
                </label>
                <div class="text-xs text-gray-500 mt-0.5">
                  If checked, the client is invoiced regardless of who paid.
                </div>

                <div v-if="form.is_client_reimbursable" class="mt-2">
                  <label class="block text-sm mb-1">Engagement</label>
                  <select
                    v-model="form.engagement_id"
                    :disabled="!editable"
                    class="w-full p-2 border border-gray-400 rounded-md"
                  >
                    <option :value="null" disabled>Select engagement...</option>
                    <option v-for="e in engagements" :key="e.id" :value="e.id">
                      {{ e.client_name ? (e.client_name + ' — ' + e.title) : e.title }}
                    </option>
                  </select>
                  <div class="text-xs text-gray-500 mt-1">
                    Only active engagements are listed. The client is implied by the engagement.
                  </div>
                </div>
              </div>

              <div v-if="canViewIsRetro" class="py-3 first:pt-0 last:pb-0">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="form.is_retro"
                    :disabled="!editable"
                  />
                  <span class="font-medium">Is Retro</span>
                </label>
                <div class="text-xs text-gray-500 mt-1">
                  Check if this expense has already been processed (record-keeping only).
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        <div class="border border-gray-300 rounded-md p-4 bg-white">
          <div class="text-lg font-semibold mb-2">3. Additional Information</div>
          <div class="space-y-4">
            <div>
              <label class="block text-sm mb-1">Tags (optional)</label>
              <TagPicker
                v-model="form.tags"
                :disabled="!editable"
                placeholder="e.g. Orlando 2026 May Rotorcraft Show"
              />
              <div class="text-xs text-gray-500 mt-1">
                Add or create tags.
              </div>
              <div v-if="record && record.group_label" class="text-xs text-gray-500 mt-1">
                <span class="font-semibold">Legacy group label:</span>
                <span class="font-mono">{{ record.group_label }}</span>
                <span class="ml-1">(read-only — superseded by tags)</span>
              </div>
            </div>

            <div>
              <label class="block text-sm mb-1">Notes (optional)</label>
              <textarea
                v-model="form.notes"
                rows="3"
                :disabled="!editable"
                class="w-full p-2 border border-gray-400 rounded-md"
                :placeholder="selectedCategoryHint || 'Additional context for the reviewer (optional).'"
              ></textarea>
              <div v-if="selectedCategoryHint" class="text-xs text-gray-500 mt-1">
                Tip: {{ selectedCategoryHint }}
              </div>
            </div>
          </div>
        </div>

      <div class="border border-gray-300 rounded-md p-4 bg-white">
        <div class="text-lg font-semibold mb-2">4. Receipts</div>
        <div class="text-xs text-gray-500 mb-2">
          Image or PDF, up to 20 MB each. A receipt is suggested but not required.
        </div>

        <ul v-if="receipts.length > 0" class="mb-2 space-y-1">
          <li
            v-for="r in receipts"
            :key="r.id"
            class="flex items-center justify-between text-sm border border-gray-200 rounded px-2 py-1"
          >
            <button
              type="button"
              class="text-accent-primary hover:underline truncate text-left"
              @click="viewingReceipt = r"
            >{{ r.original_filename || `Receipt #${r.id}` }}</button>
            <span class="text-xs text-gray-500 ml-2">{{ formatBytes(r.size_bytes) }}</span>
            <button
              v-if="editable"
              type="button"
              class="ml-2 text-xs text-red-600 hover:underline"
              @click="removeReceipt(r)"
            >Remove</button>
          </li>
        </ul>

        <div v-if="editable && record" class="flex items-center gap-2">
          <label
            class="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md border border-gray-400 hover:bg-gray-100 cursor-pointer"
          >
            <input
              ref="fileInput"
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              class="hidden"
              @change="onFilePicked"
            />
            Add Receipt
          </label>
          <span v-if="uploadingReceipt" class="text-xs text-gray-500">Uploading...</span>
        </div>
        <div v-else-if="editable && !record" class="text-xs text-gray-500">
          Save the draft first, then attach receipts.
        </div>
      </div>

      <div v-if="formError" class="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
        {{ formError }}
      </div>

      <div class="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
        <button
          v-if="editable"
          type="submit"
          class="px-3 py-1.5 text-sm rounded-md border border-gray-400 hover:bg-gray-100"
          :disabled="saving"
        >{{ saving ? 'Saving...' : 'Save Draft' }}</button>

        <button
          v-if="editable && record"
          type="button"
          class="px-3 py-1.5 text-sm rounded-md bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
          :disabled="saving"
          @click="saveAndSubmit"
        >{{ saving ? 'Working...' : 'Submit for Approval' }}</button>

        <button
          v-if="canRecall"
          type="button"
          class="px-3 py-1.5 text-sm rounded-md border border-yellow-500 text-yellow-700 hover:bg-yellow-50"
          :disabled="saving"
          @click="recall"
        >Recall (pull back before review)</button>

        <button
          v-if="record && record.status === 'draft' && canActOnRecord"
          type="button"
          class="ml-auto px-3 py-1.5 text-sm rounded-md border border-red-500 text-red-700 hover:bg-red-50"
          :disabled="saving"
          @click="deleteDraft"
        >Delete Draft</button>
      </div>

      <ExpenseHistory v-if="record && record.id" :expenseId="record.id" />
    </form>
    </div>

    <ReceiptViewerModal :receipt="viewingReceipt" @close="viewingReceipt = null" />
  </div>
</template>

<script>
export default {
  name: "expense-form",
};
</script>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import {
  createExpense,
  getExpense,
  updateExpense,
  submitExpense,
  recallExpense,
  deleteExpense,
  getExpenseGroupLabels,
} from "@/services/Expense.js";
import {
  uploadExpenseReceipt,
  deleteExpenseReceipt,
} from "@/services/ExpenseReceipt.js";
import { getExpenseCategories } from "@/services/ExpenseCategory.js";
import { getExpensePickerEngagements } from "@/services/Expense.js";
import { getMyDelegators } from "@/services/Delegation.js";
import StatusPill from "./StatusPill.vue";
import ReceiptViewerModal from "./ReceiptViewerModal.vue";
import ExpenseHistory from "./ExpenseHistory.vue";
import TagPicker from "./TagPicker.vue";
import { addTagsToExpense, removeTagFromExpense, tagErrorMessage, isRetiredTagName } from "@/services/ExpenseTag.js";
import { convertCurrencyToUSD } from "@/services/Expense.js";

const router = useRouter();
const route = useRoute();
const store = useStore();
const $toast = useToast();

const loading = ref(true);
const saving = ref(false);
const uploadingReceipt = ref(false);
const formError = ref("");
const viewingReceipt = ref(null);

const record = ref(null);
const categories = ref([]);
const engagements = ref([]);
const myDelegators = ref([]);
const groupLabelOptions = ref([]);

const form = reactive({
  date_of_expense: todayISO(),
  amount: null,
  description: "",
  notes: "",
  group_label: "",
  category_id: null,
  payment_method: "personal_paid",
  is_client_reimbursable: false,
  engagement_id: null,
  on_behalf_of_id: null,
  tags: [],
  is_retro: false,
  // Foreign currency support — the amount input always writes to
  // foreign_amount; when the currency is USD it's mirrored straight
  // into amount, otherwise amount is the converted USD equivalent.
  foreign_amount: null,
  foreign_currency_code: "USD",
  exchange_rate: null,
  exchange_rate_source: "",
  exchange_rate_date: null,
  exchange_rate_fetched_at: null,
});

const originalTagIds = ref(new Set());

// Currencies supported by the backend conversion service. Hard-coded
// from the frankfurter.app / ECB list — keep in sync with
// services/currency_conversion.py SUPPORTED_CURRENCIES.
const supportedCurrencies = [
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'ILS', name: 'Israeli Shekel' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'ISK', name: 'Icelandic Krona' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'PLN', name: 'Polish Zloty' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'ZAR', name: 'South African Rand' },
];

const conversionLoading = ref(false);
const conversionError = ref("");
let conversionDebounce = null;

const formatForeign = computed(() => {
  if (form.foreign_amount == null) return "";
  const n = Number(form.foreign_amount);
  return `${n.toFixed(2)} ${form.foreign_currency_code}`;
});
const formatUsd = computed(() => {
  if (form.amount == null) return "";
  const n = Number(form.amount);
  return n.toFixed(2);
});

const isForeignCurrency = computed(() => form.foreign_currency_code !== "USD");

function onAmountInput() {
  if (form.foreign_currency_code === "USD") {
    form.amount = form.foreign_amount;
  } else {
    scheduleConversion();
  }
}

function onCurrencyChange() {
  if (form.foreign_currency_code === "USD") {
    // Back to USD — mirror straight through, no conversion needed.
    if (conversionDebounce) clearTimeout(conversionDebounce);
    form.amount = form.foreign_amount;
    form.exchange_rate = null;
    form.exchange_rate_source = "";
    form.exchange_rate_date = null;
    form.exchange_rate_fetched_at = null;
    conversionError.value = "";
    conversionLoading.value = false;
  } else {
    scheduleConversion();
  }
}

function scheduleConversion() {
  if (conversionDebounce) clearTimeout(conversionDebounce);
  conversionDebounce = setTimeout(() => doConvert(), 350);
}

async function doConvert() {
  if (form.foreign_currency_code === "USD") return;
  if (form.foreign_amount == null || form.foreign_amount === "" || Number(form.foreign_amount) <= 0) {
    conversionError.value = "";
    return;
  }
  if (!form.foreign_currency_code) return;
  if (!form.date_of_expense) return;
  conversionLoading.value = true;
  conversionError.value = "";
  try {
    const token = store.state.auth.token;
    const result = await convertCurrencyToUSD(token, {
      amount: form.foreign_amount,
      currency: form.foreign_currency_code,
      date: form.date_of_expense,
    });
    form.amount = parseFloat(result.usd_amount);
    form.exchange_rate = result.exchange_rate;
    form.exchange_rate_source = result.source;
    form.exchange_rate_date = result.rate_date;
    form.exchange_rate_fetched_at = result.fetched_at;
  } catch (e) {
    conversionError.value = e?.response?.data?.detail || "Failed to fetch exchange rate.";
  } finally {
    conversionLoading.value = false;
  }
}

// Re-fetch when the expense date changes while a foreign currency is selected.
watch(() => form.date_of_expense, () => {
  if (isForeignCurrency.value) scheduleConversion();
});

const isNew = computed(() => !route.params.id);

// True when the current user is the record's submitter or the
// delegate who entered it. Admins/dev/superuser bypass via
// canActOnRecord below. Anyone else (including expense_client_view
// readers and managers/accounting/approver-override viewing the
// detail page) cannot drive the submit/edit/recall actions.
const canActAsSubmitter = computed(() => {
  const r = record.value;
  if (!r) return false;
  const uid = store.state.auth.user?.id;
  if (!uid) return false;
  if (r.submitter && r.submitter.id === uid) return true;
  if (r.created_by && r.created_by.id === uid) return true;
  return false;
});

const isAdminish = computed(() => {
  const p = store.state.auth.userPermissions || {};
  return !!(p.isDev || p.isSuperuser || p.isExpenseAdmin);
});

const canViewIsRetro = computed(() => {
  const p = store.state.auth.userPermissions || {};
  return !!(isAdminish.value || p.isAccountingGroup);
});

const canActOnRecord = computed(() =>
  canActAsSubmitter.value || isAdminish.value
);

const editable = computed(() => {
  if (isNew.value) return true;
  if (!record.value) return false;
  if (!canActOnRecord.value) return false;
  return record.value.status === "draft" || record.value.status === "rejected";
});

const receipts = computed(() => (record.value ? record.value.receipts || [] : []));

// Recallable when:
//   - status='submitted' and manager hasn't acted yet, OR
//   - status='manager_approved' but the record auto-advanced (no
//     manager attached) and accounting hasn't acted yet.
// Limited to the submitter / their active delegate (or admins) — a
// viewer with read-only access should never see Recall.
const canRecall = computed(() => {
  if (!canActOnRecord.value) return false;
  const r = record.value;
  if (!r) return false;
  if (r.status === 'submitted' && r.manager_decision_at === null) return true;
  if (r.status === 'manager_approved' && !r.manager
      && r.accounting_decision_at === null) return true;
  return false;
});

const selectedCategoryHint = computed(() => {
  const cat = categories.value.find((c) => c.id === form.category_id);
  return cat && cat.notes_hint ? cat.notes_hint : "";
});

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// Capture the page the user came from so the back link points there
// instead of always jumping to My Expenses (e.g. accounting queue,
// client expenses, all-expenses-admin).
const backLabel = ref("My Expenses");
const hasHistoryBack = ref(false);

// Tab keys → human-readable labels for the My Expenses back link
// (mirrors statusTabs in MyExpenses.vue). Other pages with tabs can
// be added here as the need arises.
const MY_EXPENSES_TAB_LABELS = {
  drafts: "Drafts",
  pending: "Pending Review",
  rejected: "Needs Revision",
  processed: "Processed",
  for_others: "For Others",
};

function captureBackTarget() {
  if (typeof window === "undefined") return;
  const back = window.history.state ? window.history.state.back : null;
  if (!back) return;
  hasHistoryBack.value = true;
  try {
    const resolved = router.resolve(back);
    const title = resolved && resolved.meta && resolved.meta.pageTitle;
    if (title) backLabel.value = title;
    if (resolved && resolved.name === "my-expenses") {
      const tab = resolved.query && resolved.query.tab;
      const tabLabel = tab && MY_EXPENSES_TAB_LABELS[tab];
      if (tabLabel) backLabel.value = `${title || "My Expenses"} · ${tabLabel}`;
    }
  } catch (_) {
    // Unrecognized previous route — keep the default label.
  }
}

function backToList() {
  if (hasHistoryBack.value) {
    router.back();
  } else {
    router.push({ name: "my-expenses" });
  }
}

function hydrateFormFromRecord(r) {
  form.date_of_expense = r.date_of_expense || todayISO();
  form.amount = r.amount !== null && r.amount !== undefined ? parseFloat(r.amount) : null;
  form.description = r.description || "";
  form.notes = r.notes || "";
  form.group_label = r.group_label || "";
  form.category_id = r.category ? r.category.id : null;
  form.payment_method = r.payment_method || "personal_paid";
  form.is_client_reimbursable = !!r.is_client_reimbursable;
  form.is_retro = !!r.is_retro;
  form.engagement_id = r.engagement_id || (r.engagement ? r.engagement.id : null) || null;
  form.tags = (r.tags || []).map((t) => ({ id: t.id, name: t.name }));
  originalTagIds.value = new Set(form.tags.map((t) => t.id));
  // Foreign currency fields. If the record was saved with a foreign
  // amount + currency, the amount input shows that; otherwise it's a
  // plain USD entry and the input just mirrors the USD amount.
  form.foreign_currency_code = r.foreign_currency_code || "USD";
  form.foreign_amount = r.foreign_amount !== null && r.foreign_amount !== undefined
    ? parseFloat(r.foreign_amount)
    : form.amount;
  form.exchange_rate = r.exchange_rate || null;
  form.exchange_rate_source = r.exchange_rate_source || "";
  form.exchange_rate_date = r.exchange_rate_date || null;
  form.exchange_rate_fetched_at = r.exchange_rate_fetched_at || null;
}

async function loadCategories() {
  const token = store.state.auth.token;
  const data = await getExpenseCategories(token);
  categories.value = Array.isArray(data) ? data : (data.results || []);
}

async function loadEngagements() {
  const token = store.state.auth.token;
  try {
    const engData = await getExpensePickerEngagements(token);
    engagements.value = Array.isArray(engData) ? engData : (engData.results || []);
  } catch (error) {
    console.log(error);
  }
}

async function loadGroupLabels() {
  const token = store.state.auth.token;
  try {
    const data = await getExpenseGroupLabels(token);
    groupLabelOptions.value = (data && data.group_labels) || [];
  } catch (error) {
    groupLabelOptions.value = [];
  }
}

async function loadDelegators() {
  const token = store.state.auth.token;
  try {
    const data = await getMyDelegators(token);
    myDelegators.value = (data || []).map((u) => ({
      ...u,
      full_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
    }));
  } catch (error) {
    myDelegators.value = [];
  }
}

function fullName(u) {
  if (!u) return '—';
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return name || u.username;
}

async function loadExpense() {
  if (isNew.value) {
    record.value = null;
    return;
  }
  const token = store.state.auth.token;
  const id = route.params.id;
  record.value = await getExpense(token, id);
  hydrateFormFromRecord(record.value);
}

async function initialLoad() {
  loading.value = true;
  try {
    await Promise.all([
      loadCategories(),
      loadEngagements(),
      loadDelegators(),
      loadGroupLabels(),
      loadExpense(),
    ]);
  } catch (error) {
    console.log(error);
    $toast.open({ message: "Failed to load form data", type: "error", duration: 3000 });
  }
  loading.value = false;
}

function buildPayload() {
  const payload = {
    date_of_expense: form.date_of_expense,
    amount: form.amount,
    description: form.description,
    notes: form.notes || null,
    group_label: (form.group_label || "").trim(),
    category_id: form.category_id,
    payment_method: form.payment_method,
    is_client_reimbursable: form.is_client_reimbursable,
    engagement_id: form.is_client_reimbursable ? form.engagement_id : null,
    is_retro: form.is_retro,
  };
  if (isForeignCurrency.value) {
    payload.foreign_amount = form.foreign_amount;
    payload.foreign_currency_code = form.foreign_currency_code;
    payload.exchange_rate = form.exchange_rate;
    payload.exchange_rate_source = form.exchange_rate_source;
    payload.exchange_rate_date = form.exchange_rate_date;
    payload.exchange_rate_fetched_at = form.exchange_rate_fetched_at;
  } else {
    // USD selected — clear any previously-saved foreign metadata so the
    // record stops claiming to be a foreign-currency expense.
    payload.foreign_amount = null;
    payload.foreign_currency_code = "";
    payload.exchange_rate = null;
    payload.exchange_rate_source = "";
    payload.exchange_rate_date = null;
    payload.exchange_rate_fetched_at = null;
  }
  // Only send on_behalf_of_id on CREATE (backend ignores it on updates).
  if (isNew.value && form.on_behalf_of_id) {
    payload.on_behalf_of_id = form.on_behalf_of_id;
  }
  return payload;
}

function validateClientSide() {
  formError.value = "";
  if (!form.date_of_expense) { formError.value = "Date of expense is required."; return false; }
  if (!form.amount || form.amount <= 0) { formError.value = "Amount must be greater than zero."; return false; }
  if (!form.category_id) { formError.value = "Please choose a category."; return false; }
  if (!form.description || !form.description.trim()) { formError.value = "Description is required."; return false; }
  if (form.is_client_reimbursable) {
    if (!form.engagement_id) { formError.value = "Select an engagement for client-reimbursable expenses."; return false; }
  }
  // Do not allow retired tags
  if (form.tags.some((t) => isRetiredTagName(t.name))) { formError.value = "Retro tag is not allowed. Please use Is Retro checkbox."; return false; }
  return true;
}

function extractErrorMessage(error) {
  if (error && error.response && error.response.data) {
    const data = error.response.data;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    try { return JSON.stringify(data); } catch (e) { return String(data); }
  }
  return (error && error.message) || "Request failed.";
}

async function saveDraft() {
  if (!validateClientSide()) return;
  saving.value = true;
  const token = store.state.auth.token;
  const payload = buildPayload();
  try {
    let saved;
    if (isNew.value || !record.value) {
      saved = await createExpense(token, payload);
    } else {
      saved = await updateExpense(token, record.value.id, payload);
    }
    saved = await syncTagsForRecord(saved, token);
    record.value = saved;
    $toast.open({ message: "Draft saved", type: "success", duration: 2000 });
    if (isNew.value) {
      router.replace({ name: "expense-detail", params: { id: saved.id } });
    }
  } catch (error) {
    formError.value = extractErrorMessage(error);
  }
  saving.value = false;
}

async function syncTagsForRecord(saved, token) {
  // Diff form.tags vs originalTagIds; call add-tags / remove-tag endpoints.
  const currentNames = form.tags.map((t) => t.name).filter(Boolean);
  const currentIdsKept = new Set(form.tags.map((t) => t.id).filter((x) => x != null));
  const toRemove = [...originalTagIds.value].filter((id) => !currentIdsKept.has(id));
  // Send the name of every tag the user added in this edit — both typed-new
  // (id == null) and picked-from-suggestions (id present but not original).
  // The backend resolves names case-insensitively to existing tags.
  const namesToAdd = form.tags
    .filter((t) => t.id == null || !originalTagIds.value.has(t.id))
    .map((t) => t.name);
  let next = saved;
  if (namesToAdd.length) {
    try {
      next = await addTagsToExpense(saved.id, namesToAdd, token);
    } catch (error) {
      const msg = tagErrorMessage(error);
      if (!msg) throw error;
      throw new Error(msg);
    }
  }
  for (const tagId of toRemove) {
    next = await removeTagFromExpense(saved.id, tagId, token);
  }
  if (namesToAdd.length || toRemove.length) {
    // Re-hydrate the tag-picker state from the server's authoritative response
    form.tags = (next.tags || []).map((t) => ({ id: t.id, name: t.name }));
    originalTagIds.value = new Set(form.tags.map((t) => t.id));
    // Refresh the global tag list so newly created tags appear in suggestions.
    if (currentNames.length) {
      try { await loadGroupLabels(); } catch (_) { /* noop */ }
    }
  }
  return next;
}

async function saveAndSubmit() {
  await saveDraft();
  if (formError.value || !record.value) return;
  saving.value = true;
  const token = store.state.auth.token;
  try {
    const submitted = await submitExpense(token, record.value.id);
    record.value = submitted;
    $toast.open({ message: "Submitted for approval", type: "success", duration: 2000 });
  } catch (error) {
    formError.value = extractErrorMessage(error);
  }
  saving.value = false;
}

async function recall() {
  if (!record.value) return;
  if (!window.confirm("Recall this submission? It will return to Draft so you can edit.")) return;
  saving.value = true;
  const token = store.state.auth.token;
  try {
    const recalled = await recallExpense(token, record.value.id);
    record.value = recalled;
    hydrateFormFromRecord(recalled);
    $toast.open({ message: "Recalled", type: "success", duration: 2000 });
  } catch (error) {
    formError.value = extractErrorMessage(error);
  }
  saving.value = false;
}

async function deleteDraft() {
  if (!record.value) return;
  if (!window.confirm("Delete this draft? This cannot be undone.")) return;
  const token = store.state.auth.token;
  try {
    await deleteExpense(token, record.value.id);
    $toast.open({ message: "Draft deleted", type: "success", duration: 2000 });
    router.push({ name: "my-expenses" });
  } catch (error) {
    formError.value = extractErrorMessage(error);
  }
}

async function onFilePicked(event) {
  const file = event.target.files && event.target.files[0];
  if (!file || !record.value) return;
  uploadingReceipt.value = true;
  const token = store.state.auth.token;
  try {
    const created = await uploadExpenseReceipt(token, record.value.id, file);
    record.value.receipts = [...(record.value.receipts || []), created];
    $toast.open({ message: "Receipt added", type: "success", duration: 1500 });
  } catch (error) {
    formError.value = extractErrorMessage(error);
  }
  uploadingReceipt.value = false;
  event.target.value = "";
}

async function removeReceipt(receipt) {
  if (!window.confirm("Remove this receipt?")) return;
  const token = store.state.auth.token;
  try {
    await deleteExpenseReceipt(token, receipt.id);
    record.value.receipts = record.value.receipts.filter((r) => r.id !== receipt.id);
  } catch (error) {
    formError.value = extractErrorMessage(error);
  }
}

watch(() => route.params.id, () => initialLoad());

onMounted(() => {
  captureBackTarget();
  initialLoad();
});
</script>
